import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const EXIT_DURATION_MS = 220;

export default function CelebrationVideoModal({ isOpen, onClose, videoId }) {
  const [mounted, setMounted] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const closeButtonRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  const requestClose = useCallback(() => {
    if (!mounted || closeTimeoutRef.current) return;
    setAnimateIn(false);
    const duration = prefersReducedMotion.current ? 0 : EXIT_DURATION_MS;
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      setMounted(false);
      setIframeLoaded(false);
      onClose();
    }, duration);
  }, [mounted, onClose]);

  // Reacts to isOpen going either direction, so Browser Back/Forward (which flips
  // isOpen via the URL, not via requestClose) stays in sync with X/Escape/outside-click.
  useEffect(() => {
    if (isOpen) {
      if (closeTimeoutRef.current) {
        // Was mid-exit when isOpen flipped back to true (e.g. rapid Back then Forward):
        // cancel the pending unmount and resume visible immediately.
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
        setAnimateIn(true);
      }
      setMounted(true);
    } else if (mounted) {
      requestClose();
    }
  }, [isOpen, mounted, requestClose]);

  useEffect(() => {
    if (!mounted) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const raf = requestAnimationFrame(() => setAnimateIn(true));

    const handleKeyDown = (e) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      cancelAnimationFrame(raf);
    };
  }, [mounted, requestClose]);

  useEffect(() => {
    if (animateIn) closeButtonRef.current?.focus();
  }, [animateIn]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  if (!mounted) return null;

  const motionOk = !prefersReducedMotion.current;

  const handleOverlayClick = () => {
    requestClose();
  };

  const handleVideoBoxClick = (e) => {
    e.stopPropagation();
  };

  const overlayClasses = `fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 transition-opacity ${
    motionOk ? "duration-[190ms]" : "duration-100"
  } ${animateIn ? "opacity-100" : "opacity-0"}`;

  const contentClasses = motionOk
    ? `relative w-[min(92vw,1000px)] transition-all duration-[220ms] ease-out ${
        animateIn ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-[0.96] translate-y-2"
      }`
    : `relative w-[min(92vw,1000px)] transition-opacity duration-100 ${
        animateIn ? "opacity-100" : "opacity-0"
      }`;

  return createPortal(
    <div
      className={overlayClasses}
      role="dialog"
      aria-modal="true"
      aria-label="Celebration video"
      onClick={handleOverlayClick}
    >
      <button
        ref={closeButtonRef}
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          requestClose();
        }}
        aria-label="Close video"
        className="fixed top-4 right-[18px] z-[110] flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-transparent text-white transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className={contentClasses}>
        <div
          className="aspect-video w-full overflow-hidden rounded-lg shadow-2xl"
          onClick={handleVideoBoxClick}
        >
          <iframe
            className={`h-full w-full transition-opacity duration-150 ${
              iframeLoaded ? "opacity-100" : "opacity-0"
            }`}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            title="Celebration Video"
            allow="autoplay; encrypted-media"
            allowFullScreen
            onLoad={() => setIframeLoaded(true)}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
